import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useGovernance } from '../../context/GovernanceContext';
import { DIMENSIONS } from '../../data/questions';
import { getActionSteps } from '../../utils/actionHelpers';

// ── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  todo:          { label: 'To Do',       bg: 'bg-gray-100',   text: 'text-gray-600'  },
  'in-progress': { label: 'In Progress', bg: 'bg-amber-100',  text: 'text-amber-700' },
  done:          { label: 'Done',        bg: 'bg-green-100',  text: 'text-green-700' },
};

const STATUS_CYCLE = { todo: 'in-progress', 'in-progress': 'done', done: 'todo' };

function StatusBadge({ status, onClick }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.todo;
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold cursor-pointer hover:opacity-80 transition-opacity shrink-0 ${cfg.bg} ${cfg.text}`}
    >
      {cfg.label}
    </button>
  );
}

// ── Overall progress bar ─────────────────────────────────────────────────────
function ProgressBar({ value, max, color = '#0F2D6B' }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-xs text-mid-grey shrink-0">{pct}%</span>
    </div>
  );
}

// ── Filter pill ──────────────────────────────────────────────────────────────
function FilterPill({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
        active
          ? 'bg-brand-navy text-white border-brand-navy'
          : 'bg-white text-mid-grey border-gray-200 hover:border-gray-400'
      }`}
    >
      {label}
    </button>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function ProjectManagement() {
  const { id: clientId } = useParams();
  const { clients, loading, updateAction, getClientActions } = useGovernance();

  const client = clients.find((c) => c.id === clientId);
  const allActions = getClientActions(clientId);

  const [activeFilter, setActiveFilter] = useState('all');
  const [expanded, setExpanded] = useState(new Set());

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleStepToggle = (action, stepKey) => {
    const steps = getActionSteps(action);
    const current = action.completed_steps || [];
    const next = current.includes(stepKey)
      ? current.filter((k) => k !== stepKey)
      : [...current, stepKey];

    let newStatus = action.status;
    if (next.length === steps.length && steps.length > 0) newStatus = 'done';
    else if (next.length > 0 && action.status === 'todo') newStatus = 'in-progress';
    else if (next.length < steps.length && action.status === 'done') newStatus = 'in-progress';

    updateAction(action.id, { completed_steps: next, status: newStatus });
  };

  const cycleStatus = (action) => {
    updateAction(action.id, { status: STATUS_CYCLE[action.status] || 'todo' });
  };

  if (loading) return <div className="text-mid-grey text-sm">Loading…</div>;
  if (!client) return (
    <div className="text-center py-16">
      <p className="text-mid-grey">Client not found.</p>
      <Link to="/clients" className="text-brand-teal text-sm hover:underline mt-2 block">
        ← Back to clients
      </Link>
    </div>
  );

  // ── Overall stats (unfiltered) ────────────────────────────────────────────
  const totalSteps = allActions.reduce((sum, a) => sum + getActionSteps(a).length, 0);
  const completedStepsTotal = allActions.reduce(
    (sum, a) => sum + (a.completed_steps || []).length,
    0
  );
  const doneActions = allActions.filter((a) => a.status === 'done').length;
  const outstandingActions = allActions.filter((a) => a.status !== 'done').length;

  // ── Filter logic ──────────────────────────────────────────────────────────
  const filteredActions =
    activeFilter === 'all'
      ? allActions
      : allActions.filter((a) => a.status === activeFilter);

  const filters = [
    { key: 'all', label: 'All' },
    { key: 'todo', label: 'To Do' },
    { key: 'in-progress', label: 'In Progress' },
    { key: 'done', label: 'Done' },
  ];

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-mid-grey mb-5 flex-wrap">
        <Link to="/clients" className="hover:text-brand-navy">Clients</Link>
        <span>/</span>
        <Link to={`/clients/${clientId}`} className="hover:text-brand-navy">{client.name}</Link>
        <span>/</span>
        <span className="text-brand-navy font-medium">Project Plan</span>
      </nav>

      {/* Page header */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <h1 className="text-xl font-bold text-brand-navy">Project Plan</h1>
            <p className="text-sm text-mid-grey mt-0.5">{client.name}</p>
          </div>
          <div className="text-right text-sm text-mid-grey">
            <span className="font-semibold text-brand-navy">{doneActions}</span> done ·{' '}
            <span className="font-semibold text-amber-600">{outstandingActions}</span> outstanding
          </div>
        </div>

        {/* Overall progress */}
        {totalSteps > 0 && (
          <div className="mb-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-mid-grey font-medium">Overall step progress</span>
              <span className="text-xs text-mid-grey">{completedStepsTotal} / {totalSteps} steps</span>
            </div>
            <ProgressBar value={completedStepsTotal} max={totalSteps} />
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {filters.map((f) => (
          <FilterPill
            key={f.key}
            label={f.label}
            active={activeFilter === f.key}
            onClick={() => setActiveFilter(f.key)}
          />
        ))}
      </div>

      {/* Dimension sections */}
      {filteredActions.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <p className="text-mid-grey text-sm">No actions match this filter.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {DIMENSIONS.map((dim) => {
            const dimActions = filteredActions.filter((a) => a.dimension === dim.num);
            if (dimActions.length === 0) return null;

            const dimAllActions = allActions.filter((a) => a.dimension === dim.num);
            const dimDone = dimAllActions.filter((a) => a.status === 'done').length;

            return (
              <div key={dim.num}>
                {/* Dimension header */}
                <div
                  className="flex items-center justify-between px-4 py-2.5 rounded-lg mb-2"
                  style={{ backgroundColor: `${dim.color}15` }}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="text-xs font-bold px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: `${dim.color}25`, color: dim.color }}
                    >
                      D{dim.num}
                    </span>
                    <span className="text-sm font-semibold text-brand-navy">{dim.label}</span>
                  </div>
                  <span className="text-xs text-mid-grey">
                    {dimDone}/{dimAllActions.length} actions done
                  </span>
                </div>

                {/* Action cards */}
                <div className="space-y-2 pl-1">
                  {dimActions.map((action) => {
                    const steps = getActionSteps(action);
                    const completedSteps = action.completed_steps || [];
                    const isExpanded = expanded.has(action.id);
                    const hasRunSheet = !action.is_custom && steps.length > 0;

                    return (
                      <div
                        key={action.id}
                        className="bg-white rounded-xl border border-gray-200 overflow-hidden"
                      >
                        <div className="p-4 flex items-start gap-3">
                          {/* Expand toggle */}
                          <button
                            onClick={() => toggleExpanded(action.id)}
                            className="mt-0.5 text-mid-grey hover:text-brand-navy shrink-0 text-xs"
                            title={isExpanded ? 'Collapse' : 'Expand'}
                          >
                            {isExpanded ? '▼' : '▶'}
                          </button>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold text-brand-navy">
                                {action.title}
                              </span>
                              {action.is_custom && (
                                <span className="text-xs bg-purple-50 text-purple-600 font-medium px-1.5 py-0.5 rounded">
                                  Custom
                                </span>
                              )}
                            </div>

                            {/* Step progress bar */}
                            {hasRunSheet && (
                              <div className="mt-2 flex items-center gap-2">
                                <div className="flex-1 max-w-xs">
                                  <ProgressBar
                                    value={completedSteps.length}
                                    max={steps.length}
                                    color={dim.color}
                                  />
                                </div>
                                <span className="text-xs text-mid-grey shrink-0">
                                  {completedSteps.length}/{steps.length} steps
                                </span>
                              </div>
                            )}
                          </div>

                          <StatusBadge
                            status={action.status}
                            onClick={() => cycleStatus(action)}
                          />
                        </div>

                        {/* Expanded run sheet */}
                        {isExpanded && (
                          <div className="border-t border-gray-100 bg-gray-50 px-5 py-3">
                            {action.is_custom ? (
                              <p className="text-xs text-mid-grey italic">
                                No run sheet for custom actions.
                              </p>
                            ) : steps.length === 0 ? (
                              <p className="text-xs text-mid-grey italic">No steps defined.</p>
                            ) : (
                              <ol className="space-y-3">
                                {steps.map((step) => {
                                  const checked = completedSteps.includes(step.key);
                                  return (
                                    <li key={step.key} className="flex items-start gap-2.5">
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => handleStepToggle(action, step.key)}
                                        className="mt-0.5 h-4 w-4 rounded border-gray-300 shrink-0 cursor-pointer"
                                        style={{ accentColor: dim.color }}
                                      />
                                      <div>
                                        <p
                                          className={`text-xs font-semibold ${
                                            checked ? 'line-through text-gray-400' : 'text-brand-navy'
                                          }`}
                                        >
                                          {step.title}
                                        </p>
                                        <p className="text-xs text-mid-grey mt-0.5 leading-relaxed">
                                          {step.body}
                                        </p>
                                      </div>
                                    </li>
                                  );
                                })}
                              </ol>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Custom actions not tied to any dimension shown at bottom */}
          {(() => {
            const undimensioned = filteredActions.filter(
              (a) => !DIMENSIONS.find((d) => d.num === a.dimension)
            );
            if (undimensioned.length === 0) return null;
            return (
              <div>
                <div className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-gray-50 mb-2">
                  <span className="text-sm font-semibold text-brand-navy">Other / Custom</span>
                </div>
                <div className="space-y-2 pl-1">
                  {undimensioned.map((action) => {
                    const isExpanded = expanded.has(action.id);
                    return (
                      <div
                        key={action.id}
                        className="bg-white rounded-xl border border-gray-200 overflow-hidden"
                      >
                        <div className="p-4 flex items-start gap-3">
                          <button
                            onClick={() => toggleExpanded(action.id)}
                            className="mt-0.5 text-mid-grey hover:text-brand-navy shrink-0 text-xs"
                          >
                            {isExpanded ? '▼' : '▶'}
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-brand-navy">{action.title}</span>
                              <span className="text-xs bg-purple-50 text-purple-600 font-medium px-1.5 py-0.5 rounded">
                                Custom
                              </span>
                            </div>
                          </div>
                          <StatusBadge status={action.status} onClick={() => cycleStatus(action)} />
                        </div>
                        {isExpanded && (
                          <div className="border-t border-gray-100 bg-gray-50 px-5 py-3">
                            <p className="text-xs text-mid-grey italic">No run sheet for custom actions.</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
